import Vue from 'vue';
import Requests from '@/helpers/Request';
import { toJson } from '@/helpers/EpgGuide';
import {
  buildWindow,
  normalizePrograms,
  sortTvChannels,
  TOTAL_HOURS,
  HOUR,
  MINUTE,
} from '@/helpers/EpgGuide2';

// Vuex-Modul fuer den TV-Guide v2.
//
// Unterschiede zu epg-guide (v1), die den Kern der Anforderung treffen:
//  - Laden gedrosselt (BATCH_SIZE gleichzeitig) statt alle Kanaele parallel.
//    v1 feuert per fetchEpgByChannels alle Requests gleichzeitig los; bei 60+
//    Sendern saettigt das Netz und TV kurzzeitig.
//  - Sichtbare Kanaele zuerst, der Rest im Hintergrund nachgeladen. Die Seite
//    ist damit nach dem ersten Batch bedienbar statt erst am Ende.
//  - Zwischen jedem XML-Parse wird an den Event-Loop zurueckgegeben, damit
//    Tastendruecke waehrend des Ladens durchkommen.
//  - Fenster von 48h; Sendungen ausserhalb werden verworfen.

// Gleichzeitige EPG-Requests. Klein halten: der Flaschenhals ist nicht das
// Netz, sondern das synchrone DOM-Walking beim Parsen auf der TV-CPU.
const BATCH_SIZE = 3;

// Anzahl Kanaele, die vor dem ersten Rendern geladen werden.
const FIRST_BATCH_CHANNELS = 8;

// Daten-Refresh. Programmdaten aendern sich selten; ein haeufigerer Zyklus
// erzeugt nur Last ohne Nutzen. Der Fortschrittsbalken braucht das nicht -
// dafuer gibt es den billigen now-Tick in der Seite.
const REFRESH_MS = 30 * MINUTE;

// Ab dieser Restlaufzeit des Fensters wird es neu aufgespannt, damit die
// Zeitachse nicht in die Vergangenheit laeuft.
const WINDOW_RENEW_MS = 6 * HOUR;

function yieldToEventLoop() {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export default {
  namespaced: true,
  state: () => ({
    channels: [],
    // { [epgChannelId]: [program, ...] } - nach epgChannelId, nicht nach Index:
    // die Kanalliste kann sich zwischen zwei Ladevorgaengen umsortieren.
    programsByChannel: {},
    // { [epgChannelId]: 'loading' | 'ok' | 'error' | 'empty' }
    channelStatus: {},
    window: null,
    loadedAt: null,
    isLoading: false,
    loadedCount: 0,
    error: null,
    initPromise: null,
  }),
  mutations: {
    setChannels(state, channels) {
      state.channels = channels;
    },
    setPrograms(state, { epgChannelId, programs }) {
      Vue.set(state.programsByChannel, epgChannelId, programs);
    },
    setChannelStatus(state, { epgChannelId, status }) {
      Vue.set(state.channelStatus, epgChannelId, status);
    },
    setWindow(state, window) {
      state.window = window;
    },
    setLoading(state, value) {
      state.isLoading = !!value;
    },
    setLoadedCount(state, value) {
      state.loadedCount = value;
    },
    setLoadedAt(state, value) {
      state.loadedAt = value;
    },
    setError(state, value) {
      state.error = value;
    },
    setInitPromise(state, value) {
      state.initPromise = value;
    },
    reset(state) {
      state.programsByChannel = {};
      state.channelStatus = {};
      state.loadedCount = 0;
    },
  },
  actions: {
    // Laedt Kanaele und danach die EPG-Daten gedrosselt nach.
    // Mehrfachaufrufe waehrend eines laufenden Ladevorgangs liefern dasselbe
    // Promise (wie in v1), damit ein erneutes mounten keinen Doppel-Fetch macht.
    init({ commit, dispatch, state, rootState }, options) {
      const opts = options || {};
      if (state.isLoading && state.initPromise && !opts.force) return state.initPromise;

      const run = (async () => {
        commit('setLoading', true);
        commit('setError', null);
        try {
          const window = buildWindow(new Date(), TOTAL_HOURS);
          commit('setWindow', window);
          commit('reset');

          const response = await Requests.fetchTvPackages({ deviceId: rootState.deviceId });
          const channels = sortTvChannels((response && response.package) || {});
          commit('setChannels', channels);

          // Erst die sichtbaren Kanaele - danach ist die Seite bedienbar.
          const head = channels.slice(0, FIRST_BATCH_CHANNELS);
          const tail = channels.slice(FIRST_BATCH_CHANNELS);
          await dispatch('loadChannels', { channels: head, window: window });

          commit('setLoadedAt', Date.now());
          commit('setLoading', false);

          // Rest im Hintergrund; Fehler hier duerfen die Seite nicht kippen.
          if (tail.length) {
            dispatch('loadChannels', { channels: tail, window: window }).catch((e) =>
              console.log('[epgGuide2] Nachladen fehlgeschlagen:', e)
            );
          }
        } catch (err) {
          console.log('[epgGuide2] init fehlgeschlagen:', err);
          commit('setError', String((err && err.message) || err));
          commit('setLoading', false);
          throw err;
        } finally {
          commit('setInitPromise', null);
        }
      })();

      commit('setInitPromise', run);
      return run;
    },

    // Laedt eine Kanalliste in Batches von BATCH_SIZE.
    async loadChannels({ commit, state }, { channels, window }) {
      const list = (channels || []).filter((c) => c && c.epgChannelId !== undefined);
      for (let i = 0; i < list.length; i += BATCH_SIZE) {
        // Fenster gewechselt (Refresh dazwischen) -> alten Lauf abbrechen,
        // sonst schreiben zwei Laeufe in denselben State.
        if (state.window !== window) return;

        const batch = list.slice(i, i + BATCH_SIZE);
        batch.forEach((c) =>
          commit('setChannelStatus', { epgChannelId: c.epgChannelId, status: 'loading' })
        );

        const results = await Promise.all(
          batch.map((channel) =>
            Requests.fetchEpgForChannel({ epgChannelId: channel.epgChannelId })
              .then((xml) => ({ channel: channel, xml: xml }))
              .catch((err) => {
                console.log(
                  '[epgGuide2] EPG-Fetch fehlgeschlagen fuer ' + channel.epgChannelId + ':',
                  err
                );
                return { channel: channel, xml: null };
              })
          )
        );

        // Parsen einzeln mit Yield: toJson walkt das XML-DOM synchron, und drei
        // Kanaele hintereinander blockieren auf LG WebOS lange genug, dass
        // Key-Events verloren gehen.
        for (let k = 0; k < results.length; k++) {
          const res = results[k];
          if (state.window !== window) return;
          if (!res.xml) {
            commit('setChannelStatus', {
              epgChannelId: res.channel.epgChannelId,
              status: 'error',
            });
          } else {
            let programs = [];
            try {
              programs = normalizePrograms(toJson(res.xml), window);
            } catch (e) {
              console.log('[epgGuide2] Parsen fehlgeschlagen:', e);
            }
            commit('setPrograms', {
              epgChannelId: res.channel.epgChannelId,
              programs: programs,
            });
            commit('setChannelStatus', {
              epgChannelId: res.channel.epgChannelId,
              status: programs.length ? 'ok' : 'empty',
            });
          }
          commit('setLoadedCount', state.loadedCount + 1);
          await yieldToEventLoop();
        }
      }
    },

    // Vom Refresh-Timer der Seite aufgerufen. Laedt nur neu, wenn es sich lohnt:
    // entweder ist das Zeitfenster bald abgelaufen oder die Daten sind alt.
    refresh({ state, dispatch }, options) {
      const opts = options || {};
      if (state.isLoading) return Promise.resolve(false);
      const now = Date.now();
      const windowExpiring = !state.window || state.window.end - now < WINDOW_RENEW_MS;
      const dataStale = !state.loadedAt || now - state.loadedAt >= REFRESH_MS;
      if (!opts.force && !windowExpiring && !dataStale) return Promise.resolve(false);
      console.log(
        '[epgGuide2] refresh (Fenster laeuft ab: ' + windowExpiring + ', Daten alt: ' + dataStale + ')'
      );
      return dispatch('init', { force: true }).then(() => true);
    },
  },
  getters: {
    getChannels(state) {
      return state.channels;
    },
    getWindow(state) {
      return state.window;
    },
    // Programme eines Kanals; nie undefined, damit die Seite nicht pruefen muss.
    getPrograms: (state) => (epgChannelId) => state.programsByChannel[epgChannelId] || [],
    getChannelStatus: (state) => (epgChannelId) => state.channelStatus[epgChannelId] || 'pending',
    isLoading(state) {
      return state.isLoading;
    },
    getProgress(state) {
      const total = state.channels.length;
      return { loaded: state.loadedCount, total: total };
    },
    getError(state) {
      return state.error;
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/epg-guide2.js